import { useState, useRef, useEffect } from "react";
import { MessageSquareText, X, Send, Sparkles, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import { useApp } from "../context/AppContext";

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()));

export default function ChatWidget({ lifted }) {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const greeting = lang === "ar"
    ? "أهلاً بكِ في كلاود بيوتي 🌸 أنا كلاود، كيف أساعدكِ اليوم؟"
    : "Welcome to Cloud Beauty 🌸 I'm Cloud, how can I help you today?";

  useEffect(() => {
    if (open && messages.length === 0) setMessages([{ role: "assistant", content: greeting }]);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const history = messages.filter((m) => m.content);
    const next = [...messages, { role: "user", content: text }, { role: "assistant", content: "" }];
    setMessages(next);
    setBusy(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, history }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: lang === "ar" ? "عذراً، حدث خطأ. حاولي مجدداً." : "Sorry, an error occurred." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-widget-toggle"
        className={`fixed ${lifted ? "bottom-24" : "bottom-6"} sm:bottom-6 start-4 sm:start-6 z-50 grid place-items-center h-12 w-12 sm:h-14 sm:w-14 rounded-full cb-gradient text-primary-foreground shadow-lg hover:scale-105 transition-transform`}
        aria-label="AI Assistant"
      >
        {open ? <X className="h-7 w-7" /> : <MessageSquareText className="h-7 w-7" />}
      </button>

      {open && (
        <div className={`fixed ${lifted ? "bottom-40" : "bottom-20"} sm:bottom-24 inset-x-3 sm:inset-x-auto sm:start-6 z-50 sm:w-[92vw] sm:max-w-sm rounded-3xl bg-card cb-card-shadow border border-border overflow-hidden animate-fade-up`} data-testid="chat-widget-panel" dir={lang === "ar" ? "rtl" : "ltr"}>
          <div className="cb-gradient text-primary-foreground p-4 flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-white/20"><Sparkles className="h-5 w-5" /></span>
            <div>
              <div className="font-bold leading-none">{lang === "ar" ? "مساعِدة كلاود" : "Cloud Assistant"}</div>
              <div className="text-xs opacity-80 mt-0.5">{lang === "ar" ? "متصلة الآن" : "Online now"}</div>
            </div>
          </div>
          <div ref={scrollRef} className="h-[50vh] sm:h-80 overflow-y-auto p-4 space-y-3 bg-muted/30">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-primary text-primary-foreground" : "bg-card text-foreground border border-border"
                }`}>
                  {m.content || <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={lang === "ar" ? "اكتبي رسالتكِ..." : "Type your message..."}
              className="flex-1 h-11 px-4 rounded-full bg-muted text-base sm:text-sm outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="chat-input"
            />
            <button onClick={send} disabled={busy} data-testid="chat-send-btn" className="grid place-items-center h-11 w-11 rounded-full bg-primary text-primary-foreground disabled:opacity-50">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 rtl-flip" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
